---
pageId: concept-tracker
title: What is a Tracker
description: A tracker is one activity or life area you record in Samlla — with metrics that fit how you think about it, or as a notes-only journal.
category: concepts
order: 1
lang: en
slug: concepts/tracker
updated: 2026-08-05
---

<script>
	import { TrackerTypeDemo } from '$lib/marketing/TrackerTypeDemo';
</script>

A tracker is the basic unit of observation in Samlla: one activity, habit or life area you record. Running, Sleep, Mood, Savings, Reading — each is a tracker with its own color, icon and history of entries.

A tracker works in one of two modes, chosen when you create it:

- **Metrics mode** — the tracker carries one or more metrics, the values you actually record.
- **Journal mode** — no metrics at all; every entry is free-form notes. Turn the **Enable metrics** switch off during creation to get this. The mode is fixed once the tracker exists.

## Metric types

Every metric has a type that shapes its input and its statistics:

- **Number** — numeric values: weight, count, kilometers. Carries a unit ("km, lbs, etc.").
- **Duration** — time values, entered as "1h 30m" or "90m".
- **Scale** — a rating with a defined **Start**, **End** and **Step**, like mood 1–10.
- **Yes / No** — did it happen or not.
- **Select** — one value from a predefined list, in your order.

The creation form offers Number, Duration, Scale and Select; the Yes / No type joins the list when you edit metrics on an existing tracker.

## Try it live

This is a real Samlla component, embedded in the docs:

<TrackerTypeDemo />

## Metrics in practice

- **The first metric is the primary one** — it renders large on entries and leads the statistics, so order your metrics deliberately.
- **You decide how each entry updates the tracked value**: add each entry to the total (distance run), set the current value (weight or a savings balance — an odometer-style reading), or aggregate where lower values are better.
- **Unit conversions** show the value alongside in another unit. The raw value is never changed — it is only multiplied by your factor (km → mi uses 0.621).
- Metrics you no longer need can be deactivated instead of deleted. Inactive metrics stay in the database but are hidden when adding entries.

## Schedule

A new tracker starts with a daily schedule. The schedule decides when the tracker shows up in "Today" — it never blocks manual logging from other days. You can repeat every N days, on chosen weekdays, on days of the month or on a yearly date, or remove the schedule entirely.

## Color and category

A tracker shows its own color if you set one; otherwise it inherits the color of its category. Clear the tracker's color to fall back to the category color again.

## Archiving and deleting

- **Archive** hides the tracker from your main list. All data is preserved and you can restore it anytime from Settings.
- **Move to Trash** schedules permanent deletion after 30 days — until then the tracker can still be restored from Settings.

## Plan limits

The Free plan includes 5 trackers with up to 3 metrics per tracker. Pro removes both limits.

## Examples

- **Running** — Distance (number, km), Time (duration), Average heart rate (number, bpm)
- **Sleep** — Sleep duration (duration), Sleep quality (scale 1–5)
- **Mood** — Mood, Energy and Anxiety as 1–10 scales
- **Savings** — Savings balance as a number that sets the current value
